import type { ReactNode } from 'react';
import styled from 'styled-components';

import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CellGrow } from '#src/components/searchPlayerTable/SearchTable/ui.tsx';

import { FirstCell, LastCell } from './ui.tsx';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: span var(--all-columns);
  align-items: stretch;
  @media ${laptopAndAbove} {
    background-color: var(--c-nd-50-opaque);
  }
`;

const LeftSticky = styled.div`
  position: sticky;
  z-index: 1;
  background-color: var(--c-black);
  left: 0;
  border-right: 1px solid var(--c-nd-150-opaque);
`;
const RightSticky = styled.div`
  right: 0;
  @media ${laptopAndAbove} {
    display: block;
    position: sticky;
    z-index: 1;
    background-color: var(--c-black);
  }
`;

const MarketValueRightSticky = styled(RightSticky)`
  border-left: 1px solid var(--c-nd-150-opaque);
`;

const MarketValueLastCell = styled(LastCell)`
  padding-inline: var(--market-value-padding);
`;

const Blur = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  width: var(--unit);
`;
const RightBlur = styled(Blur)`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
    left: 100%;
    background: linear-gradient(
      90deg,
      var(--c-nd-50-opaque) 0%,
      rgba(var(--c-rgb-black), 0) 100%
    );
  }
`;

const LeftBlur = styled(Blur)`
  right: 100%;
  background: linear-gradient(
    90deg,
    rgba(var(--c-rgb-black), 0) 0%,
    var(--c-nd-50-opaque) 100%
  );
  display: none;
  @media ${laptopAndAbove} {
    display: block;
  }
`;

type Props = {
  firstCell?: ReactNode;
  children: ReactNode;
  lastCell?: ReactNode;
};

export const SearchTableHeader = ({ firstCell, children, lastCell }: Props) => {
  const isLaptopOrAbove = useIsLaptop();

  return (
    <Wrapper>
      {!!firstCell && (
        <LeftSticky>
          <FirstCell>
            {firstCell}
            <RightBlur />
          </FirstCell>
        </LeftSticky>
      )}
      {children}
      {!!lastCell && (
        <>
          {!!isLaptopOrAbove && <CellGrow />}
          <MarketValueRightSticky>
            <MarketValueLastCell>
              <LeftBlur />
              {lastCell}
            </MarketValueLastCell>
          </MarketValueRightSticky>
        </>
      )}
    </Wrapper>
  );
};
